import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/api';
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Button,
  Statistic,
  Progress,
  theme,
} from 'antd';
import {
  WarningOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import { NameLink, PageHeader, PageContainer } from '../components/common';
import { resolveCategoryDisplay, type CategoryLike } from '../utils/categoryDisplay';

const { Title, Text } = Typography;
const { useToken } = theme;

interface Part {
  id: string;
  displayId: string;
  name: string;
  category?: CategoryLike;
  unit?: string | null;
  currentStockLevel?: number | null;
  optimalStockLevel?: number | null;
  unitPrice?: string | null;
}

export function StockDashboardPage() {
  const navigate = useNavigate();
  const { token } = useToken();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 全部品・消耗品データ取得
  const { data: partsData, isLoading } = trpc.parts.list.useQuery({
    page: currentPage,
    limit: pageSize,
  });

  // 部品・消耗品カテゴリ一覧取得（カテゴリ名表示用）
  const { data: partCategories = [] } = trpc.partCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  // カテゴリマップ作成（ID → カテゴリ名）
  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    partCategories.forEach(cat => {
      map[cat.id] = cat.categoryLabel;
    });
    return map;
  }, [partCategories]);

  const parts = useMemo<Part[]>(
    () =>
      Array.isArray(partsData?.items)
        ? (partsData.items as Part[]).filter((item) => Boolean(item?.id))
        : [],
    [partsData]
  );
  const total = partsData?.pagination?.total || 0;

  // 在庫状況別に分類
  const outOfStock = parts.filter(p => (p.currentStockLevel ?? 0) === 0);
  const lowStock = parts.filter(p => {
    const current = p.currentStockLevel ?? 0;
    const optimal = p.optimalStockLevel ?? 0;
    return current > 0 && current < optimal;
  });
  // 在庫総額計算
  const totalStockValue = parts.reduce((sum, part) => {
    const price = parseFloat(part.unitPrice || '0');
    const quantity = part.currentStockLevel ?? 0;
    return sum + (price * quantity);
  }, 0);

  // 在庫状況タグ
  const getStockStatusTag = (current: number | null | undefined, optimal: number | null | undefined) => {
    const currentStock = current ?? 0;
    const optimalStock = optimal ?? 0;

    if (currentStock === 0) {
      return <Tag icon={<ExclamationCircleOutlined />} color="error">在庫切れ</Tag>;
    }
    if (currentStock < optimalStock) {
      return <Tag icon={<WarningOutlined />} color="warning">在庫不足</Tag>;
    }
    return <Tag icon={<CheckCircleOutlined />} color="success">在庫あり</Tag>;
  };

  // 在庫充足率計算
  const getStockRate = (current: number | null | undefined, optimal: number | null | undefined) => {
    const currentStock = current ?? 0;
    const optimalStock = optimal ?? 0;
    if (optimalStock === 0) return 100;
    return Math.min(100, Math.round((currentStock / optimalStock) * 100));
  };

  // テーブルカラム定義
  const columns: ResponsiveColumnType<Part>[] = [
    {
      title: '部品・消耗品名',
      dataIndex: 'name',
      key: 'name',
      mobileRole: 'title',
      mobilePriority: 1,
      render: (text, record) => (
        <NameLink
          name={text}
          onClick={() => navigate(`/app/parts/${record.id}`)}
        />
      ),
    },
    {
      title: 'カテゴリ',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      mobileRole: 'tag',
      mobilePriority: 3,
      render: (category: CategoryLike) => {
        // categoryはCategoryLikeオブジェクトまたはID文字列の可能性がある
        let categoryName: string | null = null;

        if (typeof category === 'string') {
          // IDが直接入っている場合、マップから取得
          categoryName = categoryMap[category] || null;
        } else if (category && typeof category === 'object') {
          // CategoryLikeオブジェクトの場合
          const display = resolveCategoryDisplay(category);
          if (display) {
            return (
              <Tag color={display.color}>
                {display.label}
              </Tag>
            );
          }
          // オブジェクトからIDを取得してマップから名前を取得
          const rec = category as Record<string, unknown>;
          const categoryId = typeof rec.id === 'string' ? rec.id : null;
          categoryName = categoryId ? categoryMap[categoryId] : null;
        }

        if (!categoryName) {
          return null;
        }

        return <Tag>{categoryName}</Tag>;
      },
    },
    {
      title: '在庫状況',
      key: 'status',
      width: 120,
      mobileRole: 'tag',
      mobilePriority: 2,
      render: (_, record) => getStockStatusTag(record.currentStockLevel, record.optimalStockLevel),
    },
    {
      title: '在庫数',
      key: 'stock',
      width: 200,
      align: 'center',
      mobileRole: 'description',
      mobilePriority: 4,
      render: (_, record) => {
        const current = record.currentStockLevel ?? 0;
        const optimal = record.optimalStockLevel ?? 0;
        const unit = record.unit ?? '個';
        const rate = getStockRate(record.currentStockLevel, record.optimalStockLevel);
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text>
              <span style={{ fontWeight: 'bold' }}>{current}</span>
              <span style={{ color: '#999', margin: '0 4px' }}>/</span>
              <span style={{ color: '#666' }}>{optimal}</span>
              <span style={{ marginLeft: 4 }}>{unit}</span>
            </Text>
            <Progress
              percent={rate}
              size="small"
              status={rate === 0 ? 'exception' : rate < 100 ? 'active' : 'success'}
              showInfo={true}
              format={(percent) => `${percent}%`}
            />
          </Space>
        );
      },
    },
    {
      title: '在庫金額',
      key: 'value',
      width: 120,
      align: 'right',
      mobileRole: 'hidden',
      render: (_, record) => {
        const price = parseFloat(record.unitPrice || '0');
        const quantity = record.currentStockLevel ?? 0;
        const value = price * quantity;
        return value > 0 ? `¥${value.toLocaleString()}` : '-';
      },
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="在庫ダッシュボード"
        subtitle="部品・消耗品の在庫状況と不足アラートを確認します"
      />

      {/* 部品が0件の場合 - 部品登録への誘導バナー */}
      {!isLoading && total === 0 && (
        <Card
          style={{
            marginBottom: 16,
            background: token.colorInfoBg,
            border: `1px solid ${token.colorInfoBorder}`,
          }}
        >
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <AppstoreOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
            </Col>
            <Col flex={1}>
              <Title level={5} style={{ marginBottom: 4 }}>
                まず部品・消耗品を登録しましょう
              </Title>
              <Text type="secondary">
                部品・消耗品を登録すると、以下の機能が利用できるようになります。
              </Text>
            </Col>
            <Col>
              <Button type="primary" onClick={() => navigate('/app/parts')}>
                部品・消耗品を登録する
              </Button>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <BarChartOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                  <div>
                    <Text strong>在庫管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      在庫数を把握し、不足を事前に検知
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <ShoppingCartOutlined style={{ fontSize: 20, color: token.colorWarning }} />
                  <div>
                    <Text strong>発注管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      発注履歴を記録し、コストを管理
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <HistoryOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                  <div>
                    <Text strong>消費管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      使用履歴を追跡し、消耗パターンを分析
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="登録部品・消耗品数"
              value={total}
              suffix="件"
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="在庫切れ"
              value={outOfStock.length}
              suffix="件"
              valueStyle={{ color: outOfStock.length > 0 ? '#ff4d4f' : undefined }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="在庫不足"
              value={lowStock.length}
              suffix="件"
              valueStyle={{ color: lowStock.length > 0 ? '#faad14' : undefined }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="在庫総額"
              value={totalStockValue}
              prefix="¥"
              precision={0}
            />
          </Card>
        </Col>
      </Row>

      {/* 在庫不足アラート */}
      {lowStock.length > 0 && (
        <Card
          style={{ marginBottom: 16, borderColor: '#faad14' }}
          title={
            <Space>
              <WarningOutlined style={{ color: '#faad14' }} />
              <Text>在庫不足の部品・消耗品があります</Text>
            </Space>
          }
        >
          <Space wrap>
            {lowStock.slice(0, 5).map(part => (
              <Tag key={part.id} color="warning">
                {part.name} ({part.currentStockLevel}/{part.optimalStockLevel} {part.unit})
              </Tag>
            ))}
            {lowStock.length > 5 && (
              <Tag>他 {lowStock.length - 5} 件</Tag>
            )}
          </Space>
        </Card>
      )}

      {/* 在庫切れアラート */}
      {outOfStock.length > 0 && (
        <Card
          style={{ marginBottom: 16, borderColor: '#ff4d4f' }}
          title={
            <Space>
              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
              <Text>在庫切れの部品・消耗品があります</Text>
            </Space>
          }
          extra={
            <Button
              type="primary"
              danger
              icon={<ShoppingCartOutlined />}
              onClick={() => navigate('/app/purchase-orders/create')}
            >
              発注する
            </Button>
          }
        >
          <Space wrap>
            {outOfStock.map(part => (
              <Tag key={part.id} color="error">
                {part.name}
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      {/* 在庫一覧 */}
      <Card title="在庫一覧">
        <ResponsiveTable<Part>
          columns={columns}
          dataSource={parts}
          loading={isLoading}
          rowKey="id"
          mobileCardConfig={{
            titleField: (record) => (
              <a
                onClick={() => navigate(`/app/parts/${record.id}`)}
                style={{ color: '#1890ff', fontWeight: 'bold', cursor: 'pointer' }}
              >
                {record.name}
              </a>
            ),
            onClick: (record) => navigate(`/app/parts/${record.id}`),
          }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total) => `全 ${total} 件`,
            onChange: (page, newPageSize) => {
              setCurrentPage(page);
              if (newPageSize && newPageSize !== pageSize) {
                setPageSize(newPageSize);
              }
            },
            onShowSizeChange: (_current, size) => {
              setCurrentPage(1);
              setPageSize(size);
            },
          }}
        />
      </Card>
    </PageContainer>
  );
}
